/*
 * Copyright (c) 2020 wlocalhost.org.
 * All rights reserved.
 */

import { InjectionToken } from '@angular/core';

import { IForRootConf, IStructure } from '../interfaces/interfaces';
import { Structure } from '../classes/Structure';
import { ButtonBlock, DividerBlock, ImageBlock, SocialBlock, SpacerBlock, TBlocks, TableBlock, TextBlock } from '../classes/Elements';

/**
 * Some top documentation
 */

/**
 * The default social networks list provided by builder. This list is injected by {@link IP_SOCIAL_NETWORKS}.
 */
export const IP_DEFAULT_SOCIAL_NETWORKS: string[] = [
  'github',
  'instagram',
  'web',
  'snapchat',
  'youtube',
  'vimeo',
  'medium',
  'soundcloud',
  'dribble',
  'facebook',
  'twitter-x',
  'pinterest',
  'linkedin',
  'tiktok',
  'tumblr',
  'xing'
];
/**
 * The token that injects [default social networks list]{@link IP_DEFAULT_SOCIAL_NETWORKS}.
 * Use it to add or remove social networks within list.
 *
 * @example
 * {provide: IP_SOCIAL_NETWORKS, useValue: IP_DEFAULT_SOCIAL_NETWORKS.filter(network => !['github', 'instagram'].includes(network)}
 */
export const IP_SOCIAL_NETWORKS = new InjectionToken<string[]>('IP Social Networks', {
  providedIn: 'any',
  factory: () => IP_DEFAULT_SOCIAL_NETWORKS
});

/**
 * The default [Google Fonts]{@link https://fonts.google.com} list provided by builder.
 * This list is injected by {@link IP_GOOGLE_FONTS} token.
 */
export const IP_DEFAULT_GOOGLE_FONTS: string[] = [
  'Open+Sans:300,400,500,700',
  'Droid+Sans:300,400,500,700',
  'Lato:300,400,500,700',
  'Roboto:300,400,700,900',
  'Ubuntu:300,400,500,700',
  'Fira+Sans:300,400,500,700',
  'Mansalva:400'
];
/**
 * The token that injects [default google fonts]{@link IP_DEFAULT_GOOGLE_FONTS} list.
 */
export const IP_GOOGLE_FONTS = new InjectionToken<string[]>('IP Google Fonts', {
  providedIn: 'any',
  factory: () => IP_DEFAULT_GOOGLE_FONTS
});

/**
 * The default Fallback Fonts list provided by builder. This list is injected by {@link IP_FALLBACK_FONTS} token.
 *
 * A fallback font is a font that takes effect in case a [Google Font]{@link IP_DEFAULT_GOOGLE_FONTS} can't be loaded by some email
 * providers.
 */
export const IP_DEFAULT_FALLBACK_FONTS: string[] = [
  'Palatino Linotype, Book Antiqua, Palatino, serif',
  'Times New Roman, Times, serif',
  'Arial, Helvetica, sans-serif',
  'Arial Black, Gadget, sans-serif',
  'Comic Sans MS, cursive, sans-serif',
  'Impact, Charcoal, sans-serif',
  'Lucida Sans Unicode, Lucida Grande, sans-serif',
  'Tahoma, Geneva, sans-serif',
  'Trebuchet MS, Helvetica, sans-serif',
  'Verdana, Geneva, sans-serif',
  'Courier New, Courier, monospace',
  'Lucida Console, Monaco, monospace'
];
/**
 * The token that injects {@link IP_DEFAULT_FALLBACK_FONTS} list.
 */
export const IP_FALLBACK_FONTS = new InjectionToken<string[]>('IP Fallback Fonts', {
  providedIn: 'any',
  factory: () => IP_DEFAULT_FALLBACK_FONTS
});

/**
 * The default [Structures]{@link IStructure} list. This list is injected by {@link IP_STRUCTURES} token.
 */
export const IP_DEFAULT_STRUCTURES: IStructure[] = [
  new Structure('cols_1'),
  new Structure('cols_2'),
  new Structure('cols_3'),
  new Structure('cols_4'),
  new Structure('cols_12'),
  new Structure('cols_21')
];
/**
 * The token that injects {@link IP_DEFAULT_STRUCTURES} list.
 */
export const IP_STRUCTURES = new InjectionToken<typeof IP_DEFAULT_STRUCTURES>('IP Structures', {
  providedIn: 'any',
  factory: () => IP_DEFAULT_STRUCTURES
});

/**
 * The default [Blocks]{@link TBlocks} list. This list is injected by {@link IP_BLOCKS} token.
 */
export const IP_DEFAULT_BLOCKS: TBlocks[] = [
  new TextBlock(),
  new ImageBlock(),
  new ButtonBlock(),
  new DividerBlock(),
  new SpacerBlock(),
  new SocialBlock(),
  new TableBlock()
];
/**
 * The token that injects {@link IP_BLOCKS} list.
 */
export const IP_BLOCKS = new InjectionToken<typeof IP_DEFAULT_BLOCKS>('IP Blocks', {
  providedIn: 'any',
  factory: () => IP_DEFAULT_BLOCKS
});

/**
 * Default builder configuration, injected by {@link IP_CONFIG} token. See {@link IForRootConf} for more info.
 */
export const IP_DEFAULT_CONFIG: IForRootConf = Object.freeze({
  xApiKey: 't7HdQfZjGp6R96fOV4P8v18ggf6LLTQZ1puUI2tz',
  // apiPath: 'https://ngb-api.wlocalhost.org/v1',
  useSaveButton: true,
  usePreviewButton: true,
  useDownloadButton: true,
  templateListIfEmpty: true,
  uploadImageName: 'image'
});
/**
 * The token that injects [default builder configurations]{@link IP_DEFAULT_CONFIG}.
 */
export const IP_CONFIG = new InjectionToken<Partial<IForRootConf>>('IP Builder Configuration', {
  providedIn: 'root',
  factory: () => IP_DEFAULT_CONFIG
});

/**
 * The default Storage used by builder. It can be either `localStorage` or `sessionStorage`.
 */
export const IP_STORAGE_FACTORY = new InjectionToken<Storage>('IP Storage factory token', {
  providedIn: 'any',
  factory: () => sessionStorage
});

/**
 * The default storage to cache the templates list. It can be either `localStorage` or `sessionStorage`.
 */
export const IP_TEMPLATES_TEMPORARY_STORAGE = new InjectionToken<Storage>('IP Token that stores temporary templates cache', {
  providedIn: 'any',
  factory: () => sessionStorage
});
